import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import COMMON from 'constants/common';
import redirectLinks from 'constants/redirectLinks';
import ReturnButton from 'components/system/atoms/buttons/ReturnButton';
import MsgHelper from 'utils/message.helper';
import MSG from 'constants/messages';
import DecisionButton from 'components/machineConf/atoms/DecisionButton';
import TextArea from 'components/system/atoms/textBoxs/TextArea';
import ConfirmDialog from 'components/system/organisms/dialogs/ConfirmDialog';
import InfoDialog from 'components/system/organisms/dialogs/InfoDialog';
import RegisteredStationTable from 'components/maintenance/gift/RegisteredStationTable';
import { MachineGiftRegisteredDataResponseType, RegisteredStationType } from 'types/maintenance/GiftType';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';
import { useAppDispatch } from 'redux/hooks';
import Tenkey, { INITIAL_TEN_KEY } from 'components/support/organisms/supports/Tenkey';
import { TenkeyOpenEventType, TenkeyType } from 'types/support/supportType';
import TenkeyInput from 'components/support/atoms/inputs/TenkeyInput';
import UrlHelper from 'utils/url.helper';

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  content: {
    height: '598px',
    width: '1140px',
    padding: '0 16px',
  },
  btnReturn: {
    width: '87px',
  },
  topArea: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& .returnButton': {
      flex: 1,
      padding: '10px 0px 0px 0px',
      width: '224px',
      '$ p': {
        width: '87px',
      },
    },
    '& .addNewButton': {
      height: '64px',
      marginLeft: '28px',
    },
  },
  mainArea: {
    border: '1px solid #C8C8C8',
    borderRadius: '4px',
    height: '188px',
    flexDirection: 'row',
    marginTop: 12,
    padding: '0 15px',
  },
  decision: {
    textAlign: 'right',
    marginRight: 12,
    marginTop: '9px',
  },
  btnRegister: {
    fontSize: 28,
  },
  addNewButton: {
    height: '64px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 5,
  },
  inputTenType: {
    height: '48px',
    width: '140px',
    border: '1px solid #000000',
    fontSize: 23,
    '& input': {
      textAlign: 'left',
      paddingLeft: 20,
    },
  },
  labelStyle: {
    fontSize: 24,
    color: '#000000',
    marginRight: 10,
    width: 99,
  },
  labelContent: {
    textAlign: 'left',
    width: '200px',
  },
  textInputGiftName: {
    '& .outlinedInput': {
      width: 973,
      border: '1px solid #000000',
      borderRadius: 5,
      '& textarea': {
        textAlign: 'left',
        paddingLeft: 20,
        fontSize: 23,
        lineHeight: 1.1,
        height: 101,
      },
    },
  },
  labelStationRegistered: {
    fontSize: 24,
    marginTop: '5px',
  },
  contentText: { fontSize: '24px' },
}));

type NewGiftValueType = {
  giftName: string;
  giftPrice: string;
  versionNo: string;
};

type ParameterType = {
  id: string;
};

type GiftUpdateResultType = {
  result: boolean;
};

/* *************    constants   ************* */
const TENKEY_OPTION = {
  GIFT_PRICE: {
    mode: COMMON.TENKEY_MODE.INTEGER,
    integerSize: 4,
    scaleSize: 0,
    maxValue: 1100,
    minValue: 0,
  },
};

// messages
const confirmMsgSave: string = MsgHelper.messageFormat(MSG.INF231);
const messageSaveSuccess: string = MsgHelper.messageFormat(MSG.INF309, '景品詳細', '登録景品一覧');

const UpdateGift: React.FC<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const [tenkey, setTenkey] = React.useState(INITIAL_TEN_KEY.tenkey);
  const [openSaveConfirmDlg, setOpenSaveConfirmDlg] = React.useState(false);
  const [openInfoDialog, setOpenInfoDialog] = React.useState(false);
  const [giftKey, setGiftKey] = React.useState<string>('');
  const [registeredStationData, setRegisteredStationData] = React.useState<RegisteredStationType>({
    giftInfo: null,
    rows: [],
  });

  const [inputValue, setInputValue] = useState<NewGiftValueType>({
    giftName: '',
    giftPrice: '',
    versionNo: '',
  });

  const [originalValue, setOriginalValue] = useState<NewGiftValueType>({
    giftName: '',
    giftPrice: '',
    versionNo: '',
  });

  const handleDecisionTenkey = useCallback(
    (value: string) => {
      setInputValue({ ...inputValue, giftPrice: value });
      setTenkey(INITIAL_TEN_KEY.tenkey);
    },
    [inputValue],
  );

  const handleCloseTenkey = useCallback(() => {
    setTenkey(INITIAL_TEN_KEY.tenkey);
  }, []);

  const initialTenkey: TenkeyType = {
    ...INITIAL_TEN_KEY.tenkey,
    open: true,
    onDecision: handleDecisionTenkey,
    onClose: handleCloseTenkey,
  };

  // Tenkey - Initial height
  const handleOpenInitialGiftPrice: TenkeyOpenEventType = useCallback(() => {
    setTenkey({
      ...initialTenkey,
      anchorPosition: { top: 100, left: 385 },
      name: 'giftPrice',
      option: TENKEY_OPTION.GIFT_PRICE,
      value: String(inputValue?.giftPrice),
    });
  }, [inputValue]);

  /* ************ API ************ */
  const getGiftRegisteredInfo = async (id: string) => {
    const http = new HttpConnection({ dispatch });
    const data = await http.get<MachineGiftRegisteredDataResponseType>(
      UrlHelper.convertQueryUrl(REST_API.MAINTENANCE.GET_MACHINE_GIFT_REGISTERED_DATA, { giftId: id }),
    );
    const machineGiftRegisteredDataInfo: RegisteredStationType = registeredStationData;
    if (data.giftInfo !== undefined) {
      machineGiftRegisteredDataInfo.giftInfo = {
        giftId: data.giftInfo.id,
        versionNo: data.giftInfo.versionNo,
        giftName: data.giftInfo.giftName,
        giftCost: data.giftInfo.giftCost ?? '',
        updatedAt: data.giftInfo.updatedAt,
      };

      machineGiftRegisteredDataInfo.rows = data.registeredStation;
      setInputValue({
        giftName: machineGiftRegisteredDataInfo.giftInfo.giftName,
        giftPrice: machineGiftRegisteredDataInfo.giftInfo.giftCost,
        versionNo: machineGiftRegisteredDataInfo.giftInfo.versionNo,
      });
      setOriginalValue({
        giftName: machineGiftRegisteredDataInfo.giftInfo.giftName,
        giftPrice: machineGiftRegisteredDataInfo.giftInfo.giftCost,
        versionNo: machineGiftRegisteredDataInfo.giftInfo.versionNo,
      });
      setRegisteredStationData({ ...machineGiftRegisteredDataInfo });
    }
  };

  const updateGiftInfo = async () => {
    const http = new HttpConnection({ dispatch });
    const data = await http.put<GiftUpdateResultType>(
      UrlHelper.convertQueryUrl(REST_API.MAINTENANCE.GET_MACHINE_GIFT_REGISTERED_DATA, { giftId: giftKey }),
      { versionNo: inputValue.versionNo, giftName: inputValue.giftName, giftCost: inputValue.giftPrice },
    );
    if (data.result) {
      setOpenInfoDialog(true);
    }
  };

  useEffect(() => {
    const giftKeyState =
      location.state !== undefined && location.state !== null ? (location.state as ParameterType).id : '';
    setGiftKey(giftKeyState);
    getGiftRegisteredInfo(giftKeyState);
  }, [location]);

  const handleClickGroupConfTop = () => {
    navigate(redirectLinks.MAINT_GIFT_LIST);
  };

  const handleSaveButton = () => {
    if (inputValue.giftName !== originalValue.giftName || inputValue.giftPrice !== originalValue.giftPrice) {
      setOpenSaveConfirmDlg(true);
    } else {
      navigate(redirectLinks.MAINT_GIFT_LIST);
    }
  };
  const handleChangeInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const text = ev.target.value;
    // 入力から改行を削除
    const cleanedText = text.replace(/[\r\n]/g, '');
    setInputValue({
      ...inputValue,
      [ev.target.name]: cleanedText,
    });
  };

  const handleSubmitData = useCallback(() => {
    setOpenSaveConfirmDlg(false);
    updateGiftInfo();
  }, [giftKey, inputValue]);

  const handleCloseInfoDialog = useCallback(() => {
    setOpenInfoDialog(false);
    handleClickGroupConfTop();
  }, []);

  return (
    <>
      <Box className={classes.content}>
        <Box className={classes.topArea}>
          <Box className="returnButton">
            <ReturnButton onClick={handleClickGroupConfTop}>
              <p className={classes.btnReturn}>登録景品一覧</p>
            </ReturnButton>
          </Box>
        </Box>
        <Box className={classes.mainArea}>
          <div className={classes.inputRow}>
            <div className={classes.labelStyle}>
              <div className={classes.labelContent}>景品名称</div>
            </div>
            <div>
              <TextArea
                id="giftName"
                name="giftName"
                value={inputValue.giftName}
                onChange={handleChangeInput}
                className={classes.textInputGiftName}
              />
            </div>
          </div>
          <div className={classes.inputRow}>
            <div className={classes.labelStyle}>
              <div className={classes.labelContent}>原価</div>
            </div>
            <div>
              <TenkeyInput
                id="giftPrice"
                name="giftPrice"
                className={classes.inputTenType}
                isSelected={tenkey.name === 'giftPrice'}
                leftRight="2"
                value={String(inputValue.giftPrice)}
                onOpen={handleOpenInitialGiftPrice}
              />
            </div>
          </div>
        </Box>
        <div>
          <div className={classes.labelStationRegistered}>登録中のステーション</div>
          <RegisteredStationTable rows={registeredStationData.rows} />
        </div>
        <Grid className={classes.decision}>
          <DecisionButton onClick={handleSaveButton}>
            <span className={classes.btnRegister}>更新</span>
          </DecisionButton>
        </Grid>
      </Box>
      <ConfirmDialog
        open={openSaveConfirmDlg}
        msg={confirmMsgSave}
        onOk={handleSubmitData}
        onCancel={() => setOpenSaveConfirmDlg(false)}
        addClass={{
          contentText: classes.contentText,
        }}
      />
      <InfoDialog open={openInfoDialog} msg={messageSaveSuccess} closeFunc={handleCloseInfoDialog} />
      <Tenkey tenkey={tenkey} />
    </>
  );
};

export default UpdateGift;
